import {computed, inject, Injectable} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslocoService} from '@jsverse/transloco';
import {DirectusService, EvaluationSubCriteriaDirectus} from '@frontend/directus';

export const RFQ_FEE_SC_NUMBERS = {
  service: 91,
  implementation: 92,
  integrationSetup: 93,
  other: 94,
} as const;

export type RfqFeeCategory = keyof typeof RFQ_FEE_SC_NUMBERS;

export interface RfqFeeCategoryLabel {
  title?: string;
  tooltip?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RfqFeeCategoryService {
  private readonly directusService = inject(DirectusService);
  private readonly transloco = inject(TranslocoService);

  private readonly activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  private readonly subCriteria = toSignal(this.directusService.loadSubCriteria(), {
    initialValue: [] as EvaluationSubCriteriaDirectus[],
  });

  private readonly labels = computed(() => {
    const language = this.activeLang();
    const subCriteriaByNumber = new Map(this.subCriteria().map((sub) => [sub.sc_number, sub]));

    return Object.fromEntries(
      Object.entries(RFQ_FEE_SC_NUMBERS).map(([category, scNumber]) => {
        const translations = subCriteriaByNumber.get(scNumber)?.translations ?? [];
        const translation =
          translations.find((entry) => entry.language === language) ?? translations[0];
        return [category, {title: translation?.name, tooltip: translation?.tooltip}];
      }),
    ) as Record<RfqFeeCategory, RfqFeeCategoryLabel>;
  });

  findLabel(category: RfqFeeCategory): RfqFeeCategoryLabel {
    return this.labels()[category];
  }
}
